import { useState, useEffect } from 'react'

interface LocationData {
  latitude: number
  longitude: number
  accuracy?: number
  city?: string
  country?: string
}

interface LocationError {
  code: number
  message: string
}

interface UseLocationResult {
  location: LocationData | null
  error: LocationError | null
  loading: boolean
  requestLocation: () => void
  hasPermission: boolean | null
}

export const useLocation = (): UseLocationResult => {
  const [location, setLocation] = useState<LocationData | null>(null)
  const [error, setError] = useState<LocationError | null>(null)
  const [loading, setLoading] = useState(false)
  const [hasPermission, setHasPermission] = useState<boolean | null>(null)

  const checkPermission = async () => {
    if (!navigator.geolocation) {
      setError({
        code: 0,
        message: 'Geolocation is not supported by this browser'
      })
      return false
    }

    try {
      const permission = await navigator.permissions.query({ name: 'geolocation' })
      setHasPermission(permission.state === 'granted')
      return permission.state === 'granted'
    } catch (err) {
      // Fallback for browsers that don't support permissions API
      setHasPermission(null)
      return null
    }
  }

  const requestLocation = () => {
    if (!navigator.geolocation) {
      setError({
        code: 0,
        message: 'Geolocation is not supported by this browser'
      })
      return
    }

    setLoading(true)
    setError(null)

    const options: PositionOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 300000 // 5 minutes
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords
        
        try {
          // Try to get city name from reverse geocoding
          const city = await getCityFromCoordinates(latitude, longitude)
          
          setLocation({
            latitude,
            longitude,
            accuracy,
            city,
            country: 'South Africa'
          })
          setHasPermission(true)
        } catch (geocodeError) {
          // Set location without city name if geocoding fails
          setLocation({
            latitude,
            longitude,
            accuracy,
            country: 'South Africa'
          })
          setHasPermission(true)
        }
        
        setLoading(false)
      },
      (err) => {
        let errorMessage = 'An unknown error occurred'
        
        switch (err.code) {
          case err.PERMISSION_DENIED:
            errorMessage = 'Location access denied by user'
            setHasPermission(false)
            break
          case err.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable'
            break
          case err.TIMEOUT:
            errorMessage = 'Location request timed out'
            break
        }
        
        setError({
          code: err.code,
          message: errorMessage
        })
        setLoading(false)
      },
      options
    )
  }

  // Simple reverse geocoding using a free service
  const getCityFromCoordinates = async (lat: number, lng: number): Promise<string | undefined> => {
    try {
      // Using a simple approach - in production, you'd use a proper geocoding service
      // This is a basic implementation for demo purposes
      const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`
      )
      
      if (response.ok) {
        const data = await response.json()
        return data.city || data.locality || data.principalSubdivision
      }
    } catch (error) {
      console.warn('Geocoding failed:', error)
    }
    
    return undefined
  }

  // Check permission on mount
  useEffect(() => {
    checkPermission()
  }, [])

  // Auto-request location if we have permission
  useEffect(() => {
    if (hasPermission && !location && !loading) {
      requestLocation()
    }
  }, [hasPermission])

  return {
    location,
    error,
    loading,
    requestLocation,
    hasPermission
  }
}

// Utility function to get default location for demo purposes
export const getDefaultLocation = (): LocationData => {
  return {
    latitude: -26.2041,
    longitude: 28.0473,
    city: 'Johannesburg',
    country: 'South Africa'
  }
}

// Calculate distance between two points
export const calculateDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number => {
  const R = 6371 // Radius of the Earth in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLng = (lng2 - lng1) * Math.PI / 180
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  const distance = R * c // Distance in kilometers
  return Math.round(distance * 10) / 10 // Round to 1 decimal place
}